import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, BrewersUnitOfWork, SessionInformationService, UtilityService } from 'app/core/core.module';
import { QuickBooksService } from 'app/core/data/quickbooks.service';
import { InvoiceStatus, InvoiceType, QuickBooksTaskResponse } from 'app/core/model/entity-model';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert } from 'devextreme/ui/dialog';
import { BsModalService } from 'ngx-bootstrap/modal';
import { JournalEntryDialogComponent } from '../journal-entry-dialog/journal-entry-dialog.component';

@Component({
    standalone: false,
    selector: 'app-quickbooks',
    templateUrl: './quickbooks.component.html',
    styleUrls: ['./quickbooks.component.scss'],
})
export class QuickBooksComponent implements OnInit {
    uow = inject(BrewersUnitOfWork);
    router = inject(Router);
    appInfo = inject(AppInformationService);
    sessionInfo = inject(SessionInformationService);
    route = inject(ActivatedRoute);
    qbService = inject(QuickBooksService);
    modalService = inject(BsModalService);

    data: any[];
    filter = {
        selectedEntityType: 'Invoice',
    };
    types = ['Customer', 'Invoice', 'Credit', 'Payment', 'Vendor', 'Journal Entry'];
    invoiceStatuses: InvoiceStatus[];
    paymentMethods: any[];
    currentUserData: any;
    responses: any[];
    processing: boolean = false;
    canSeeQB: boolean = false;
    isBusy = false;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('quickbooksSyncResultsGrid') quickbooksSyncResultsGrid: DxDataGridComponent;

    async ngOnInit() {
        this.processing = true;
        await this.checkParams();
        this.invoiceStatuses = this.uow.lookups.invoiceStatuses as InvoiceStatus[];
        this.paymentMethods = this.uow.lookups.paymentMethods;
        this.canSeeQB = true;

        const filter = this.sessionInfo.getItem('quickbooks-filter');
        if (filter) this.filter = filter;
        else this.filter.selectedEntityType = 'Invoice';
        await this.getData();
        await this.getResponseData();

        this.processing = false;
    }

    async checkParams() {
        this.route.queryParams.subscribe((params) => {
            this.checkStatus(params);
        });
    }

    async checkStatus(params?: any, ignoreProcessing?: boolean) {
        let isAuthorized;
        if (params?.code && params?.realmId && params?.state) {
            isAuthorized = await this.qbService.authorizeCallback(params.code, params.realmId, params.state);
            this.router.navigate(['/quickbooks/overview'], { replaceUrl: true });
        } else {
            isAuthorized = await this.qbService.isAuthorized();
        }
        const status = isAuthorized ? 'Connected' : 'Disconnected';

        if (!ignoreProcessing) {
            this.processing = false;
        }
        if (isAuthorized) this.sessionInfo.setItem('currentUserData', isAuthorized.authData);
    }

    typeChanged = async (e) => {
        if (this.processing) return;
        await this.getData();
        this.sessionInfo.setItem('quickbooks-filter', this.filter);
    };
    async getData() {
        this.appInfo.pleaseWaitShow();

        if (this.filter.selectedEntityType === 'Journal Entry') this.data = await this.uow.getDataToExport('JournalEntries', 'creditAccount, debitAccount');
        if (this.filter.selectedEntityType === 'Customer') this.data = await this.uow.getDataToExport('Customers');
        if (this.filter.selectedEntityType === 'Vendor') this.data = await this.uow.getDataToExport('Vendors');
        if (this.filter.selectedEntityType === 'Invoice' || this.filter.selectedEntityType === 'Credit') {
            const data = await this.uow.getDataToExport('Invoices', ['customer', 'invoiceLines', 'referencedInvoice']);
            const typeId = this.filter.selectedEntityType === 'Invoice' ? InvoiceType.Types.Invoice : InvoiceType.Types.Credit;
            this.data = data.filter((invoice) => invoice.invoiceTypeId === typeId);
        }
        if (this.filter.selectedEntityType === 'Payment') this.data = await this.uow.getDataToExport('Payments', ['customer', 'paymentLines.invoice']);
        this.appInfo.pleaseWaitHide();
    }

    async connectToQuickbooks() {
        this.currentUserData = await this.qbService.authorize();
        if (this.currentUserData.authorizeUrl && this.currentUserData.authorizeUrl.trim().length > 0) document.location.href = this.currentUserData.authorizeUrl;
    }

    disconnect() {
        alert(`Disconnected`, this.appInfo.appTitle);
        this.sessionInfo.setItem('currentUserData', null);
    }

    async getAccountsOrProducts(mode: string) {
        const params = this.sessionInfo.getItem('currentUserData');
        if (!params) {
            await alert(`User Credentials Not Found: Please Connect to QuickBooks.`, this.appInfo.appTitle);
            this.appInfo.pleaseWaitHide();
            return;
        }

        this.appInfo.pleaseWaitShow();
        const ids = [];
        const result = await this.qbService.exportToQuickBooksByIds(mode, params.Code, params.RealmId, params.State, params.AccessToken, params.RefreshToken, ids);
        await this.getData();
        await this.getResponseData();
        this.appInfo.pleaseWaitHide();
    }

    async syncCustomer() {
        const params = this.sessionInfo.getItem('currentUserData');
        if (!params) {
            await alert(`User Credentials Not Found: Please Connect to QuickBooks.`, this.appInfo.appTitle);
            return;
        }
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        console.log(selectedRows);
        if (!selectedRows || selectedRows.length === 0) return;

        // this.appInfo.pleaseWaitShow();
        const ids = selectedRows.map((x) => {
            return x.id;
        });

        const test = await this.qbService.exportToQuickBooksByIds('CustomerSync', params.Code, params.RealmId, params.State, params.AccessToken, params.RefreshToken, ids);
        await this.getData();
        await this.getResponseData();
        // this.appInfo.pleaseWaitHide();
    }

    async getResponseData() {
        this.appInfo.pleaseWaitShow();
        const responses = (await this.uow.getQBTaskResponses()) as QuickBooksTaskResponse[];
        // responses.forEach((response: any) => {
        //     response.customerName =
        //         response.type === 'Customer'
        //             ? response.customer.displayName
        //             : response.type === 'Invoice' || response.type === 'Credit'
        //             ? (!!response.invoice ? response.invoice.customer?.displayName : response.customer.displayName) //customers can be created when exporting invoices
        //             : response.type === 'Vendor'
        //             ? response.vendor.displayName
        //             : response.type === 'Payment'
        //             ? response.payment.customer.displayName
        //             : '';
        // });
        this.responses = responses;
        // console.log(this.responses)
        this.appInfo.pleaseWaitHide();
    }

    async refresh() {
        await this.getData();
        await this.getResponseData();
    }

    async export(all?: boolean) {
        let selectedRows: any;

        if (all) {
            const confirmResult = await confirm(`Are you sure you want to Export ALL?`);
            if (!confirmResult) return;
            selectedRows = this.data;
        } else {
            selectedRows = this.dataGrid.instance.getSelectedRowsData();
            if (!selectedRows) return;
        }

        const params = this.sessionInfo.getItem('currentUserData');
        if (!params) {
            await alert(`User Credentials Not Found: Please Connect to QuickBooks.`, this.appInfo.appTitle);
            return;
        }

        this.appInfo.pleaseWaitShow();
        const ids = selectedRows.map((x) => {
            return x.id;
        });

        const test = await this.qbService.exportToQuickBooksByIds(
            this.filter.selectedEntityType,
            params.Code,
            params.RealmId,
            params.State,
            params.AccessToken,
            params.RefreshToken,
            ids,
        );
        await this.getData();
        await this.getResponseData();
        this.appInfo.pleaseWaitHide();
    }

    async markAsExported() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length !== 1 || selectedRows[0].quickBooksId) return;

        this.appInfo.pleaseWaitShow();
        selectedRows.forEach((x) => {
            if (!x.quickBooksId) {
                x.quickBooksId = 'markedExported';
                x.quickBooksLastExport = new Date();
            }
        });
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    openResponsesData(data: any, dataField: string) {
        switch (dataField) {
            case 'customerId':
            case 'customerDisplayName':
                const customerId =
                    data.type === 'Customer'
                        ? data.customerId
                        : data.type === 'Payment'
                          ? data.payment.customerId
                          : data.type === 'Invoice' || data.type === 'Credit'
                            ? data.invoice.customerId
                            : null;
                if (customerId) this.router.navigate([`/customers/${customerId}`]);
                break;
            case 'invoiceId':
            case 'invoice.invoiceNumber':
                this.router.navigate([`/invoices/${data.invoiceId}`]);
                break;
            case 'paymentId':
            case 'payment.referenceNumber':
                this.router.navigate([`/payments/${data.paymentId}`]);
                break;
            case 'vendorId':
            case 'displayName':
                const vendorId = data.type === 'Vendor' ? data.vendorId : null;
                if (vendorId) this.router.navigate([`/vendors/${vendorId}`]);
                break;
        }
    }
    openFromQueue(data: any, dataField: string) {
        // console.log(data);
        // console.log(dataField);
        switch (dataField) {
            case 'id':
                if (this.filter.selectedEntityType === 'Customer') this.router.navigate([`/customers/${data.id}`]);
                else if (this.filter.selectedEntityType === 'Invoice' || this.filter.selectedEntityType === 'Credit') this.router.navigate([`/sales/${data.id}`]);
                else if (this.filter.selectedEntityType === 'Payment') this.router.navigate([`/payments/${data.id}`]);
                break;
            case 'displayName':
                if (this.filter.selectedEntityType === 'Customer') this.router.navigate([`/customers/${data.id}`]);
                else if (this.filter.selectedEntityType === 'Vendor') this.router.navigate([`/vendors/${data.id}`]);

                break;
            case 'customer.displayName':
                this.router.navigate([`/customers/${data.customer.id}`]);
                break;
            case 'paymentLines[0].invoice.invoiceNumber':
                this.router.navigate([`/invoices/${data.paymentLines[0].invoice.id}`]);
                break;
            case 'invoiceNumber':
                this.router.navigate([`/invoices/${data.id}`]);
                break;
            case 'referencedInvoice.invoiceNumber':
                this.router.navigate([`/invoices/${data.referencedInvoiceId}`]);
                break;

            case 'referenceNumber':
                this.router.navigate([`/payments/${data.id}`]);
                break;
        }
    }

    async addJournalEntry() {
        const modalRef = this.modalService.show(JournalEntryDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {},
        });

        modalRef.content?.result.subscribe(async (result) => {
            if (result && result.success) {
                await this.appInfo.pleaseWaitShow();
                await this.getData();
                await this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }
}
